import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';

import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange, validateRangeAllowZero } from '../../range-validate';
import { EpfCalculatorService, EpfResult, EPF_CONSTRAINTS } from './epf-calculator.service';

@Component({
  selector: 'app-epf-calculator',
  standalone: true,
  imports: [FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './epf-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./epf-calculator.scss']
})
export class EpfCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(EpfCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('growthRef')    growthRef!:    ElementRef<HTMLCanvasElement>;

  readonly C = EPF_CONSTRAINTS;
  readonly Math = Math;

  currentAge = 28;
  retirementAge = 58;
  monthlyBasicSalary = 30_000;
  salaryGrowthPercent = 8;
  employeePercent = 12;
  employerPercent = 3.67;
  annualInterestRate = 8.25;
  currentBalance = 0;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     EpfResult | null = null;

  get tenureYears(): number { return this.retirementAge - this.currentAge; }

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.currentAge, this.C.age.min, this.C.age.max, 'Current age');
    if (e1) errors['currentAge'] = e1;
    const e2 = validateRange(this.retirementAge, this.C.age.min, 60, 'Retirement age');
    if (e2) errors['retirementAge'] = e2;
    else if (this.retirementAge <= this.currentAge) errors['retirementAge'] = 'Retirement age must be greater than current age.';
    const e3 = validateRange(this.monthlyBasicSalary, this.C.basicSalary.min, this.C.basicSalary.max, 'Basic salary');
    if (e3) errors['monthlyBasicSalary'] = e3;
    const e4 = validateRangeAllowZero(this.salaryGrowthPercent, this.C.salaryGrowth.min, this.C.salaryGrowth.max, 'Salary growth');
    if (e4) errors['salaryGrowthPercent'] = e4;
    const e5 = validateRange(this.employeePercent, this.C.employeePercent.min, this.C.employeePercent.max, 'Employee contribution');
    if (e5) errors['employeePercent'] = e5;
    const e6 = validateRange(this.employerPercent, this.C.employerPercent.min, this.C.employerPercent.max, 'Employer contribution');
    if (e6) errors['employerPercent'] = e6;
    const e7 = validateRange(this.annualInterestRate, this.C.interestRate.min, this.C.interestRate.max, 'Interest rate');
    if (e7) errors['annualInterestRate'] = e7;
    const e8 = validateRangeAllowZero(this.currentBalance, this.C.currentBalance.min, this.C.currentBalance.max, 'Current EPF balance');
    if (e8) errors['currentBalance'] = e8;
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'currentAge' || field === 'retirementAge') {
      (this as any)[field] = Math.floor((this as any)[field] || 0);
    }
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = {
      currentAge: true, retirementAge: true, monthlyBasicSalary: true, salaryGrowthPercent: true,
      employeePercent: true, employerPercent: true, annualInterestRate: true, currentBalance: true
    };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(
      Math.floor(this.currentAge), Math.floor(this.retirementAge), this.monthlyBasicSalary,
      this.salaryGrowthPercent, this.employeePercent, this.employerPercent,
      this.annualInterestRate, this.currentBalance
    );
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Employee Contribution', value: r.totalEmployeeContribution, color: '#3b82f6' },
        { label: 'Employer Contribution', value: r.totalEmployerContribution, color: '#f59e0b' },
        { label: 'Interest Earned', value: r.totalInterest, color: '#10b981' }
      ], 'Maturity Value');
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.years.map(row => `Age ${row.age}`),
        [
          { label: 'EPF Balance', data: r.years.map(row => Math.round(row.balance)), color: '#3b82f6' }
        ]
      );
    }, 60);
  }

  reset(): void {
    this.currentAge         = 28;
    this.retirementAge      = 58;
    this.monthlyBasicSalary = 30_000;
    this.salaryGrowthPercent= 8;
    this.employeePercent    = 12;
    this.employerPercent    = 3.67;
    this.annualInterestRate = 8.25;
    this.currentBalance     = 0;
    this.formErrors         = {};
    this.touched            = {};
    this.showFormErrors     = false;
    this.calculated         = false;
    this.result             = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
